import {
  bookingRefundOffers,
  bookingRefundOffersPatch,
  bookingReleaseOffers,
  bookingReleaseOffersPatch,
} from "../../app/api/cancelBooking";
import { calculatePrice } from "../../Lib/Utils/validationUtil";
import { extractBookedNolAncillaries, extractBookedNolPassIds } from "../../Lib/Utils/nolCard";
import {
  AdmissionStatusEnum,
  AncillaryStatusEnum,
} from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookingResponse } from "../../Orchestrator/Types/Index";
import {
  IBookingRefundOffersResponse,
  IBookingReleaseOffersResponse,
  IRefundOffer,
  IReleaseOffer,
} from "../../Orchestrator/Types/Types/BookingTypes";

/** What the confirm dialog shows before the refund is committed. */
export interface OnwardRefundQuote {
  fulfillmentIds: string[];
  refundableAmount: number;
  refundFee: number;
  /** refundableAmount + refundFee — what was originally paid for these fulfillments. */
  paidAmount: number;
  /** Shuttles hold admissions (a travel right) and must be released first. */
  needsRelease: boolean;
}

/**
 * Backend settle-order race guard, copied verbatim from ConfirmCancelClickAction:
 * the release/refund PATCHes 500-error when fired back-to-back.
 * ponytail: fixed sleep, replace with a status poll if the backend ever exposes one.
 */
const SETTLE_DELAY_MS = 500;
const settleDelay = () => new Promise((resolve) => setTimeout(resolve, SETTLE_DELAY_MS));

/**
 * Fulfillment ids for a booked shuttle offer, matched the same way the live
 * cancel path does: an admission's `id` IS the fulfillment id, so intersect
 * the offer's FULFILLED admissions with `booking.fulfillments`.
 *
 * Empty result = nothing paid for on this offer (freshly added in checkout,
 * or an add whose payment was abandoned) — the caller should plain-DELETE it
 * instead of refunding.
 *
 * `passengerIds` (BOOKING passenger ids, not externalRefs) narrows the result
 * to one rider's admissions. A shuttle offer holds ONE admission per rider, so
 * without it a refund of "this passenger" cancels the whole party's tickets —
 * FL-641. Omit it and the whole offer comes back, which is what every
 * ownership check here and in `sessionRefundLegs` wants.
 *
 * An EMPTY array is a real filter meaning "nobody", NOT "everybody": a UI that
 * loses its selection must refund nothing rather than the lot. Only `undefined`
 * means unfiltered.
 *
 * Fail safe on attribution: with a filter given, an admission carrying no
 * `passengerIds` is dropped. It cannot be proven to belong to the selected
 * rider, and refunding what we cannot attribute is the failure being fixed.
 * Same rule as `ownedByExchangingPassenger` (sessionRefundLegs.ts).
 */
export function shuttleFulfillmentIds(
  booking: IBookingResponse | null,
  /**
   * One offer, or EVERY offer a merged card covers. A card that draws two
   * purchases on one departure as a single row must refund both, or the half it
   * cannot name stays paid for and unreachable — see [[mapBookedShuttlesToSideLists]].
   */
  offerId: string | string[],
  passengerIds?: string[]
): string[] {
  const wantedOffers = (Array.isArray(offerId) ? offerId : [offerId]).filter(Boolean);
  if (wantedOffers.length === 0) return [];
  const offers = booking?.booking?.bookedOffers || [];
  const matched = offers.filter((o) => o?.offerId && wantedOffers.includes(o.offerId));
  if (matched.length === 0) return [];
  const fulfillmentIds = new Set(
    (booking?.booking?.fulfillments || []).map((f) => f?.id).filter(Boolean)
  );
  const wantedPassengers = passengerIds ? new Set(passengerIds) : null;
  return matched
    .flatMap((offer) => offer.admissions || [])
    .filter(
      (admission) =>
        admission?.status === AdmissionStatusEnum.FULFILLED &&
        admission?.id &&
        fulfillmentIds.has(admission.id) &&
        (!wantedPassengers ||
          ((admission as { passengerIds?: string[] })?.passengerIds || []).some((passengerId) =>
            wantedPassengers.has(passengerId)
          ))
    )
    .map((admission) => admission.id);
}

/*
 * DELIBERATELY ADMISSION IDS ONLY — no walk of `admission.ancillaryRefs`.
 *
 * Mobile does add each FULFILLED ancillary hanging off a matched admission
 * (e2e_shuttle_helper.dart:577-583, "a fulfillment left out of /refund-offers
 * is neither refunded nor cancelled"). The reason not to port it is simply that
 * SHUTTLE OFFERS CARRY NO ANCILLARIES — mobile says so in that same docblock,
 * calling the walk "insurance". An unreachable branch on a money path cannot be
 * tested, cannot be verified against a payload that does not exist, and is the
 * kind of speculative generality that reads as covered when it is not.
 *
 * NOT because the change-date path would strand it — that concern was raised
 * and is false. `ownedByExchangingPassenger` resolves ancillaries perfectly
 * well (`passengerIdsForFulfillmentId` scans `offer.ancillaries` explicitly),
 * ancillaries do carry `passengerIds` (docs/retailer.json,
 * BookingSnapshotAncillary), and the shipped Nol-on-change-date refund depends
 * on exactly that — `nolFulfillmentIds` returns ancillary ids and
 * `refundIdsForDirection` runs them through it. If it dropped them, that
 * feature would be a silent no-op.
 *
 * So if shuttle offers ever DO carry ancillaries: port the walk, and the
 * existing attribution already handles them.
 */

/**
 * The BOOKING passenger ids that hold a REFUNDABLE admission on this shuttle
 * offer — i.e. exactly the riders `shuttleFulfillmentIds` can resolve an id for.
 *
 * The picker and the confirm dialog are both built from this rather than from
 * `ownedEntry.passengerRefs`: that list comes from every NON-TERMINAL admission
 * (normalizeBookedShuttle), so it also names riders whose admission is merely
 * PREBOOKED — an addition abandoned mid-payment. Offering to refund them shows
 * a name the refund then cannot include, and the amount is the only tell.
 *
 * One derivation for ids and names together; deriving them separately is how
 * the dialog ends up naming somebody who is not in the refund.
 *
 * ASSUMES ONE ADMISSION PER RIDER, which is what the backend sends. A single
 * admission naming two riders would produce two selectable rows that cannot be
 * separated — `shuttleFulfillmentIds` keeps such an admission if EITHER rider
 * is named (dropping it would strand a paid ticket), so ticking one refunds
 * both and only the amount would say so. If that shape ever appears, the rows
 * have to be grouped and locked together here, not silently offered apart.
 */
export function shuttleRefundablePassengerIds(
  booking: IBookingResponse | null,
  /** One offer, or every offer a merged card covers. */
  offerId: string | string[]
): string[] {
  const wantedOffers = (Array.isArray(offerId) ? offerId : [offerId]).filter(Boolean);
  if (wantedOffers.length === 0) return [];
  const matched = (booking?.booking?.bookedOffers || []).filter(
    (o) => o?.offerId && wantedOffers.includes(o.offerId)
  );
  if (matched.length === 0) return [];
  const fulfillmentIds = new Set(
    (booking?.booking?.fulfillments || []).map((f) => f?.id).filter(Boolean)
  );
  const passengerIds: string[] = [];
  for (const admission of matched.flatMap((offer) => offer.admissions || [])) {
    if (
      admission?.status !== AdmissionStatusEnum.FULFILLED ||
      !admission?.id ||
      !fulfillmentIds.has(admission.id)
    ) {
      continue;
    }
    for (const passengerId of (admission as { passengerIds?: string[] })?.passengerIds || []) {
      if (passengerId && !passengerIds.includes(passengerId)) passengerIds.push(passengerId);
    }
  }
  return passengerIds;
}

/**
 * Fulfillment ids for the leg's booked Nol passes. Nol is an ancillary, not an
 * admission — it never appears in `booking.fulfillments`, but the backend
 * accepts ancillary ids as fulfillment ids on the refund endpoints (same
 * convention ProbeRefundOffersAction relies on).
 */
/**
 * True when the leg's Nol passes exist but NONE of them are FULFILLED — i.e.
 * attached to the booking and never paid for. Plain DELETE is safe there (it
 * refunds nothing because nothing was paid), and it is the only way out of an
 * otherwise stuck card: no fulfillments means no refund quote, and the
 * aftersales path refuses to guess.
 */
/**
 * True when the shuttle offer has admissions but they are all RELEASED — the
 * signature of a refund that died between the release PATCH and the refund
 * PATCH. The passenger has lost the travel right AND the money, and no retry
 * can recover it from the UI because RELEASED admissions never appear in
 * `booking.fulfillments`. Callers must say so specifically rather than showing
 * a generic error the passenger will keep retrying.
 */
export function shuttleIsReleasedNotRefunded(
  booking: IBookingResponse | null,
  offerId: string
): boolean {
  if (!offerId) return false;
  const offer = (booking?.booking?.bookedOffers || []).find((o) => o?.offerId === offerId);
  const admissions = offer?.admissions || [];
  return (
    admissions.length > 0 &&
    admissions.every((a) => (a as { status?: string })?.status === AdmissionStatusEnum.RELEASED)
  );
}

export function nolIsUnpaidOnly(
  booking: IBookingResponse | null,
  legId: string
): boolean {
  if (!legId) return false;
  const legAncillaryIds = new Set(
    extractBookedNolAncillaries(booking, legId).map((a) => a.ancillaryId)
  );
  // Passes bought via add-fmlm-travel-passes are ADMISSIONS, not ancillaries, so
  // the ancillary walk below cannot see them. Getting this wrong is a MONEY bug
  // in the dangerous direction: an unseen paid pass reads "unpaid only", and the
  // caller then plain-DELETEs a pass the passenger paid for, with no refund.
  const legAdmissionIds = new Set(extractBookedNolPassIds(booking, legId));
  if (legAncillaryIds.size === 0 && legAdmissionIds.size === 0) return false;
  const legStatuses: string[] = [];
  for (const offer of booking?.booking?.bookedOffers || []) {
    for (const anc of (offer as any)?.ancillaries || []) {
      if (anc?.id && legAncillaryIds.has(anc.id)) legStatuses.push(anc.status);
    }
    for (const adm of (offer as any)?.admissions || []) {
      if (adm?.id && legAdmissionIds.has(adm.id)) legStatuses.push(adm.status);
    }
  }
  return (
    legStatuses.length > 0 &&
    legStatuses.every((st) => st !== AncillaryStatusEnum.FULFILLED)
  );
}

/**
 * Does the leg hold a pass that is NOT CURRENTLY FULFILLED?
 *
 * Not "unpaid" literally: REFUNDED and RELEASED answer true too, and those were
 * paid for once. The name is about what the caller may assume — that every pass
 * on the leg is live and priced — and any non-FULFILLED status breaks that.
 *
 * The `some` twin of `nolIsUnpaidOnly`'s `every`, and the distinction is the
 * whole point: an all-or-nothing answer cannot describe a leg carrying one live
 * pass and one abandoned mid-payment, and a caller that hides its only control
 * on the strength of the live one strands the other — whose sole exit is the
 * plain DELETE behind that same control.
 *
 * Fail-open by construction: every extra status it catches keeps a control
 * VISIBLE rather than hiding one.
 */
export function nolHasUnpaidPass(
  booking: IBookingResponse | null,
  legId: string
): boolean {
  if (!legId) return false;
  const legPassIds = new Set([
    ...extractBookedNolAncillaries(booking, legId).map((a) => a.ancillaryId),
    ...extractBookedNolPassIds(booking, legId),
  ]);
  if (legPassIds.size === 0) return false;
  for (const offer of booking?.booking?.bookedOffers || []) {
    for (const pass of [
      ...((offer as any)?.ancillaries || []),
      ...((offer as any)?.admissions || []),
    ]) {
      if (!pass?.id || !legPassIds.has(pass.id)) continue;
      if (pass.status !== AncillaryStatusEnum.FULFILLED) return true;
    }
  }
  return false;
}

export function nolFulfillmentIds(
  booking: IBookingResponse | null,
  legId: string,
  /**
   * BOOKING passenger ids to narrow to. Omit for every pass on the leg.
   *
   * ACPT-006: this was unscoped, and `quoteOnwardRemoval`/`confirmOnwardRemoval`
   * had no way to express "only this rider" for Nol even though they already did
   * for the shuttle (`shuttleFulfillmentIds`' third argument). Refunding one
   * passenger's pass therefore cancelled every pass on the leg — the same defect
   * as FL-641 on the shuttle, left unfixed here.
   */
  passengerIds?: string[]
): string[] {
  if (!legId) return [];
  const wantedPassengers = passengerIds ? new Set(passengerIds) : undefined;
  const statusById = new Map<string, string>();
  for (const offer of booking?.booking?.bookedOffers || []) {
    for (const anc of (offer as any)?.ancillaries || []) {
      if (anc?.id) statusById.set(anc.id, anc.status);
    }
    // The FMLM shape: the pass is an admission, and the booking issues it its
    // OWN fulfillment whose id IS the admission id (verified on INT booking
    // c7434561 — fulfillment 2f7cbced…, control number TONBZDQHYP8J). That is
    // what /refund-offers takes, so a paid pass refunds through the same
    // machinery as a shuttle; there is no FMLM-specific refund endpoint (the
    // only DELETE is pre-fulfillment).
    for (const adm of (offer as any)?.admissions || []) {
      if (adm?.id) statusById.set(adm.id, adm.status);
    }
  }
  const legPassIds = [
    ...extractBookedNolAncillaries(booking, legId, wantedPassengers).map((a) => a.ancillaryId),
    ...extractBookedNolPassIds(booking, legId, wantedPassengers),
  ];
  return legPassIds.filter((id) => statusById.get(id) === AncillaryStatusEnum.FULFILLED);
}

/**
 * The BOOKING passenger ids holding a REFUNDABLE Nol pass on this leg — i.e.
 * exactly the riders `nolFulfillmentIds` can resolve an id for.
 *
 * The Nol counterpart of [[shuttleRefundablePassengerIds]], and built for the
 * same reason: the refund picker and the confirm dialog must be derived from
 * the ids that will actually be sent, or the dialog names somebody the refund
 * then cannot include.
 *
 * Passes with no `passengerIds` contribute no rider here AND are excluded by
 * `nolFulfillmentIds` once a scope is given, so the two agree: such a pass is
 * refundable only by an unscoped call, never by a picked one.
 */
export function nolRefundablePassengerIds(
  booking: IBookingResponse | null,
  legId: string
): string[] {
  if (!legId) return [];
  const refundable = new Set(nolFulfillmentIds(booking, legId));
  if (refundable.size === 0) return [];
  const passengerIds: string[] = [];
  for (const offer of (booking?.booking?.bookedOffers || []) as any[]) {
    for (const pass of [...(offer?.admissions || []), ...(offer?.ancillaries || [])]) {
      if (!pass?.id || !refundable.has(pass.id)) continue;
      for (const passengerId of pass?.passengerIds || []) {
        if (passengerId && !passengerIds.includes(passengerId)) passengerIds.push(passengerId);
      }
    }
  }
  return passengerIds;
}

const sumRefundOffers = (offers: IRefundOffer[]) =>
  offers.reduce(
    (acc, offer) => ({
      refundableAmount: acc.refundableAmount + (calculatePrice(offer?.refundableAmount) || 0),
      refundFee: acc.refundFee + (calculatePrice(offer?.refundFee) || 0),
    }),
    { refundableAmount: 0, refundFee: 0 }
  );

/**
 * Price the cancellation WITHOUT committing it. POST /refund-offers only
 * generates offers — nothing settles until the PATCH — which is what lets
 * ProbeRefundOffersAction fire it speculatively on every manage-seating entry.
 *
 * Returns null when the backend produced no refund offer, so the caller can
 * surface an error instead of showing a misleading "you get back AED 0".
 */
export async function quoteOnwardRefund(
  bookingId: string,
  fulfillmentIds: string[],
  needsRelease: boolean
): Promise<OnwardRefundQuote | null> {
  // OSDM 400s on an empty fulfillmentIds array — never POST one.
  if (!bookingId || fulfillmentIds.length === 0) return null;

  const response: any = await bookingRefundOffers(
    { fulfillmentIds, overruleCode: null, refundDate: new Date().toISOString() },
    bookingId
  );
  if (response?.code !== 200) return null;

  const answer: IBookingRefundOffersResponse = response.answer;
  const offers = answer?.refundOffers || [];
  if (offers.length === 0) return null;

  const { refundableAmount, refundFee } = sumRefundOffers(offers);
  return {
    fulfillmentIds,
    refundableAmount,
    refundFee,
    paidAmount: refundableAmount + refundFee,
    needsRelease,
  };
}

/**
 * Commit the cancellation, mirroring ConfirmCancelClickAction's proven order:
 * release the travel right (shuttle only), then refund the money.
 *
 * The refund offers are re-POSTed rather than reusing the quote's ids: for a
 * shuttle the release in between invalidates them, and for Nol a second POST
 * is a cheap way to keep one code path. Throws on any non-200 so the caller
 * keeps the card on screen instead of clearing it against a failed refund.
 */
export async function commitOnwardRefund(
  bookingId: string,
  quote: OnwardRefundQuote
): Promise<void> {
  const { fulfillmentIds, needsRelease } = quote;
  if (!bookingId || fulfillmentIds.length === 0) {
    throw new Error("commitOnwardRefund: missing bookingId or fulfillmentIds");
  }

  if (needsRelease) {
    const releaseResponse: any = await bookingReleaseOffers({ fulfillmentIds }, bookingId);
    if (releaseResponse?.code !== 200) {
      throw new Error("release-offers failed");
    }
    const released: IBookingReleaseOffersResponse = releaseResponse.answer;
    for (const offer of released?.releaseOffers || ([] as IReleaseOffer[])) {
      const patched: any = await bookingReleaseOffersPatch(offer.id, bookingId);
      if (patched?.code !== 200) {
        throw new Error(`release-offers PATCH failed for ${offer.id}`);
      }
      await settleDelay();
    }
    await settleDelay();
  }

  const refundResponse: any = await bookingRefundOffers(
    { fulfillmentIds, overruleCode: null, refundDate: new Date().toISOString() },
    bookingId
  );
  if (refundResponse?.code !== 200) {
    throw new Error("refund-offers failed");
  }
  const refunded: IBookingRefundOffersResponse = refundResponse.answer;
  const refundOfferIds = (refunded?.refundOffers || []).map((offer) => offer.id);
  if (refundOfferIds.length === 0) {
    throw new Error("refund-offers returned no offers");
  }
  // The PATCHes are what actually move the money — an unchecked failure here
  // turns a diagnosable API error into a silent no-op.
  for (const refundOfferId of refundOfferIds) {
    const patched: any = await bookingRefundOffersPatch(refundOfferId, bookingId);
    if (patched?.code !== 200) {
      throw new Error(`refund-offers PATCH failed for ${refundOfferId}`);
    }
    await settleDelay();
  }
}
